/** The engine's flow endpoints. */

import { engineBase } from "../api";
import type { FlowPick, FlowSpec } from "./flowMath";

async function post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${engineBase()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return (await res.json()) as T;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${engineBase()}${path}`);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return (await res.json()) as T;
}

export interface FlowSummary {
  id: number;
  name: string;
  updatedAt: string;
}

export interface CurateResult {
  picks: FlowPick[];
  warnings: string[];
  pool: number;
  end: number;
  error?: string;
}

export const listFlows = async (): Promise<FlowSummary[]> => {
  try {
    const res = await get<{ flows?: FlowSummary[] }>("/api/flows");
    return Array.isArray(res.flows) ? res.flows : [];
  } catch {
    return [];
  }
};

export const loadFlow = (id: number) =>
  get<{ flow?: { id: number; name: string; spec: FlowSpec }; error?: string }>(`/api/flows?id=${id}`);

export const saveFlow = (name: string, spec: FlowSpec, id?: number | null) =>
  post<{ ok?: boolean; flow?: { id: number; name: string; spec: FlowSpec }; error?: string }>(
    "/api/flows/save", { id: id ?? undefined, name, spec });

export const deleteFlow = (id: number) => post<{ ok?: boolean }>("/api/flows/delete", { id });

export async function curateFlow(
  body: { spec: FlowSpec; start?: number; history?: unknown[]; until?: number; maxPicks?: number },
  signal?: AbortSignal
): Promise<CurateResult> {
  try {
    const res = await post<CurateResult>("/api/flows/curate", body, signal);
    return { ...res, picks: Array.isArray(res.picks) ? res.picks : [], warnings: res.warnings ?? [] };
  } catch (err) {
    if (signal?.aborted) throw err;
    return { picks: [], warnings: [], pool: 0, end: 0, error: err instanceof Error ? err.message : String(err) };
  }
}

export const commitExport = (name: string, trackIds: string[], targetSeconds: number) =>
  post<{ ok?: boolean; playlistId?: number; error?: string }>(
    "/api/flows/export", { name, trackIds, targetSeconds });

export interface SeedOption {
  id: string;
  title: string;
  artist: string;
}

/** Songs for the seed picker: the Library's own search, anywhere in the library. */
export async function searchSeeds(ast: unknown, signal: AbortSignal): Promise<SeedOption[]> {
  const res = await fetch(`${engineBase()}/api/tracks?limit=8&q=${encodeURIComponent(JSON.stringify(ast))}`, { signal });
  if (!res.ok) return [];
  const body = (await res.json()) as { tracks?: SeedOption[] };
  return (body.tracks ?? []).map((t) => ({ id: String(t.id), title: t.title, artist: t.artist }));
}
